import { useEffect, useRef, useState, type InputHTMLAttributes } from 'react'
import { cn } from '@/lib/cn'

type NativeProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'min' | 'max' | 'step' | 'type'>

interface NumberFieldProps extends NativeProps {
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  step?: number
  /** Committed when the field is left empty. Defaults to `min`, then 0. */
  fallback?: number
  /** Select the contents on focus so typing replaces rather than appends. */
  selectOnFocus?: boolean
}

/**
 * A number input that does not fight the person typing in it.
 *
 * Clamping on every keystroke is the obvious implementation and it is wrong:
 * with a range of 3-18, typing "15" clamps the intermediate "1" up to 3, so the
 * next keystroke makes "35", which clamps to 18. The value you asked for is
 * unreachable and the one you get is nonsense.
 *
 * Instead the text is held locally while editing. A parsed value is committed
 * only once it lands inside the range, so intermediate states like "1" or ""
 * simply leave the previous value alone. Anything still out of range is clamped
 * on blur, when the person has finished typing.
 */
export function NumberField({
  value, onChange, min, max, step, fallback, selectOnFocus = true, className, onFocus, onBlur, ...rest
}: NumberFieldProps) {
  const [text, setText] = useState(() => String(value))
  const editing = useRef(false)

  // Mirror external changes, but never yank the field out from under someone.
  useEffect(() => {
    if (!editing.current) setText(String(value))
  }, [value])

  const clamp = (n: number): number => {
    if (min !== undefined && n < min) return min
    if (max !== undefined && n > max) return max
    return n
  }

  const inRange = (n: number): boolean =>
    (min === undefined || n >= min) && (max === undefined || n <= max)

  function handleChange(raw: string) {
    setText(raw)
    const parsed = Number(raw)
    // Commit only complete, in-range values; "", "-", and "1" (mid-typing)
    // are held locally until blur.
    if (raw.trim() !== '' && Number.isFinite(parsed) && inRange(parsed)) {
      onChange(parsed)
    }
  }

  function handleBlur(event: React.FocusEvent<HTMLInputElement>) {
    editing.current = false
    const parsed = Number(text)
    const resolved =
      text.trim() === '' || !Number.isFinite(parsed)
        ? (fallback ?? min ?? 0)
        : clamp(parsed)

    setText(String(resolved))
    if (resolved !== value) onChange(resolved)
    onBlur?.(event)
  }

  return (
    <input
      {...rest}
      type="number"
      inputMode="numeric"
      min={min}
      max={max}
      step={step}
      value={text}
      onChange={(e) => handleChange(e.target.value)}
      onFocus={(e) => {
        editing.current = true
        if (selectOnFocus) e.target.select()
        onFocus?.(e)
      }}
      onBlur={handleBlur}
      className={cn(
        'rounded-xl border border-ink-600/80 bg-ink-900/70 px-2 py-1 text-center font-mono text-sm tabular-nums text-ink-100',
        'transition-colors hover:border-ink-500 focus:border-gold-600 focus:outline-none disabled:opacity-60',
        className,
      )}
    />
  )
}
